/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

/**
 * Form state, payload and validation for the Partner Benefit Program tab.
 *
 * Kept apart from policy_configuration.js because the two share nothing but the
 * draft toggle and the policy name: a program draws on its own rule vocabulary
 * (CardPolicyRule::PARTNERSHIP_RULE_KEYS) and its own create contract.
 */

export const PROVIDER_POLICY_LAYER = "provider";
export const RECIPIENT_POLICY_LAYER = "recipient";

export const GROUP_ENTITY_TYPE = "RestaurantGroup";
export const RESTAURANT_ENTITY_TYPE = "Restaurant";

export const POLICY_LAYER_OPTIONS = [
  {
    label: "Provider policy",
    value: PROVIDER_POLICY_LAYER,
    helperText: "Owned by the Provider organization — defines the boundaries of the program.",
  },
  {
    label: "Recipient policy",
    value: RECIPIENT_POLICY_LAYER,
    helperText: "Owned by the Recipient organization — chooses its audience within the provider's boundaries.",
  },
];

export const PARTNERSHIP_HELPER_TEXT =
  "A partnership is not directional. Choose the benefit direction below — reversing Provider "
  + "and Recipient creates another program, never another partnership.";

// Mirrors CardPolicyRule override_modes. `configurable` is offered only on the keys
// that declare an envelope (CardPolicyRule::CONFIGURABLE_RULE_KEYS); a boolean has
// no range, so its control drops to the first two.
export const OVERRIDE_MODE_OPTIONS = [
  { label: "Locked", value: "locked" },
  { label: "Restrictive", value: "restrictive" },
  { label: "Configurable", value: "configurable" },
];

export const LOCKED_RESTRICTIVE_OPTIONS = OVERRIDE_MODE_OPTIONS.filter(
  (option) => option.value !== "configurable"
);

export const ALL_AUDIENCE_MODE = "all";
export const RESTAURANT_AUDIENCE_MODE = "restaurant_level";
export const CARD_AUDIENCE_MODE = "card_level";
export const JOB_ROLE_AUDIENCE_MODE = "job_role_level";

// Mirrors CardPolicyRule.normalize_audience_title: job-role titles are stored this way,
// so an option keyed any other way never matches a saved selection. Ruby's strip only
// removes ASCII whitespace and NUL, unlike JS trim().
export const normalizeAudienceTitle = (title) => `${title || ""}`
  .replace(/^[\t\n\v\f\r \0]+|[\t\n\v\f\r \0]+$/g, "")
  .replace(/ +/g, " ")
  .toLowerCase();

// Older rows may hold un-normalized titles, so they are normalized on load to match the options.
const audienceValuesFrom = (definition) => {
  const values = Array.isArray(definition?.value) ? definition.value : [];

  return definition?.mode === JOB_ROLE_AUDIENCE_MODE
    ? [...new Set(values.map(normalizeAudienceTitle))]
    : values;
};

// `selectionLabel` names the picker that appears beside the mode; `all` has none,
// because it names no selection at all.
export const AUDIENCE_MODE_OPTIONS = [
  { label: "All Employees", value: ALL_AUDIENCE_MODE, selectionLabel: "" },
  { label: "Selected Restaurants", value: RESTAURANT_AUDIENCE_MODE, selectionLabel: "Restaurants" },
  { label: "Selected Perq Level / Name", value: CARD_AUDIENCE_MODE, selectionLabel: "Perq Cards" },
  { label: "Role / Position", value: JOB_ROLE_AUDIENCE_MODE, selectionLabel: "Job titles" },
];

// The bridge Api::V1::AudiencePreviewQuery::ENTITY_TYPES_BY_AUDIENCE_MODE names.
// `all` resolves against the recipient group itself — every guest of the recipient.
export const AUDIENCE_PREVIEW_ENTITY_TYPES = {
  [ALL_AUDIENCE_MODE]: "restaurant_group",
  [RESTAURANT_AUDIENCE_MODE]: "restaurant",
  [CARD_AUDIENCE_MODE]: "discount_card",
  [JOB_ROLE_AUDIENCE_MODE]: "job_role",
};

export const PROGRAM_WEEKDAY_OPTIONS = [
  { label: "Mon", value: "monday" },
  { label: "Tue", value: "tuesday" },
  { label: "Wed", value: "wednesday" },
  { label: "Thu", value: "thursday" },
  { label: "Fri", value: "friday" },
  { label: "Sat", value: "saturday" },
  { label: "Sun", value: "sunday" },
];

export const BENEFIT_CARD_IDS_KEY = "partnership_benefit_card_ids";
export const AUDIENCE_MODE_KEY = "audience_mode";
export const EXCEPTIONS_PERMITTED_KEY = "exceptions_permitted";
export const EXCEPTION_GUEST_IDS_KEY = "exception_network_guest_ids";
export const USAGE_FREQUENCY_KEY = "program_usage_frequency";
export const PROGRAM_START_DATE_KEY = "program_start_date";
export const PROGRAM_END_DATE_KEY = "program_end_date";
export const PROGRAM_VALID_DAYS_KEY = "program_valid_days";
export const PROGRAM_TIME_WINDOW_KEY = "program_valid_time_window";

// The rules a provider states an override_mode on. The pair is driven by one control:
// exceptions_permitted and its id list are coupled in both directions, so a mode that
// differed between them could never be satisfied.
export const OVERRIDABLE_RULE_KEYS = [
  EXCEPTIONS_PERMITTED_KEY,
  USAGE_FREQUENCY_KEY,
  PROGRAM_START_DATE_KEY,
  PROGRAM_END_DATE_KEY,
  PROGRAM_VALID_DAYS_KEY,
  PROGRAM_TIME_WINDOW_KEY,
];

// CardPolicyRule::CONFIGURABLE_RULE_KEYS — the keys whose control offers a range.
export const CONFIGURABLE_RULE_KEYS = [
  USAGE_FREQUENCY_KEY,
  PROGRAM_START_DATE_KEY,
  PROGRAM_END_DATE_KEY,
  PROGRAM_VALID_DAYS_KEY,
  PROGRAM_TIME_WINDOW_KEY,
];

const DEFAULT_OVERRIDE_MODE = "locked";

const DEFAULT_OVERRIDE_MODES = OVERRIDABLE_RULE_KEYS.reduce(
  (modes, ruleKey) => ({ ...modes, [ruleKey]: DEFAULT_OVERRIDE_MODE }),
  {}
);

// The envelope a `configurable` rule moves within, shaped like the value it bounds.
const INITIAL_OVERRIDE_CONSTRAINTS = {
  usage_number_of_time: "",
  usage_number_of_days: "",
  program_start_date: "",
  program_end_date: "",
  program_valid_days: [],
  program_valid_time_start: "",
  program_valid_time_end: "",
};

/**
 * The Partner Benefit Program form state. Held apart from the group / restaurant
 * form state so neither can leak a field into the other's payload; the draft
 * toggle and the policy name stay shared and live on the standard state.
 */
export const INITIAL_PARTNER_PROGRAM_CONFIGURATION = {
  policy_layer: "",
  partnership_id: "",
  // The whole dropdown row, kept so the provider / recipient controls can read the
  // two sides without re-fetching the list they were chosen from.
  partnership: null,
  provider_key: "",
  recipient_key: "",
  benefit_card_ids: [],
  description: "",
  exceptions_permitted: false,
  exception_network_guest_ids: [],
  usage_number_of_time: "",
  usage_number_of_days: "",
  program_start_date: "",
  program_end_date: "",
  program_valid_days: [],
  program_valid_time_start: "",
  program_valid_time_end: "",
  audience_mode: ALL_AUDIENCE_MODE,
  audience_values: [],
  override_modes: { ...DEFAULT_OVERRIDE_MODES },
  override_constraints: { ...INITIAL_OVERRIDE_CONSTRAINTS },
  // Recipient only: the contract from GET /policy_engines/recipient_prefill, which
  // says per rule what may be edited and what may be sent. Never re-derived in JS.
  prefill: null,
  // Edit only: the policy's persisted status and rule set, so an update sends
  // `status` and `rules` only where something actually moved.
  status: "",
  persisted_rules: [],
};

const isTextFieldSet = (value) => `${value ?? ""}`.trim() !== "";

/**
 * @description Joins a polymorphic program side into a single select value, since a
 * group id and a restaurant id can collide.
 * @param entityType The side's model name.
 * @param entityId The side's record id.
 * @returns The composite key, or an empty string when either half is missing.
 */
export const buildEntityKey = (entityType, entityId) => (
  entityType && isTextFieldSet(entityId) ? `${entityType}:${entityId}` : ""
);

/**
 * @description Splits a composite program-side key back into its type and id.
 * @param entityKey A key built by buildEntityKey.
 * @returns { type, id }, or null when the key is blank or malformed.
 */
export const parseEntityKey = (entityKey) => {
  const [entityType, entityId] = `${entityKey || ""}`.split(":");

  return entityType && entityId ? { type: entityType, id: Number(entityId) } : null;
};

/**
 * @description The two sides of a partnership dropdown row, dropping a member row
 * the endpoint could not resolve.
 * @param partnership A row from the active_partners_list dropdown.
 * @returns The resolvable sides, as { entity_id, entity_type, entity_name }.
 */
export const partnershipEntities = (partnership) => (
  [partnership?.entity_one, partnership?.entity_two]
    .filter((entity) => entity?.entity_type && entity?.entity_id)
);

/**
 * @description Which program shapes a partnership can carry, and which side each
 * one pins. A group-to-group partnership runs in either direction, so both controls
 * are open; a group-to-restaurant one is DIRECTED — the program is only ever authored
 * with the group providing (CardPolicy::ALLOWED_PROGRAM_PAIRS), so both sides are fixed.
 * @param partnership A row from the active_partners_list dropdown.
 * @returns { entities, isGroupToGroup, pinnedProvider, pinnedRecipient }.
 */
export const derivePartnershipSides = (partnership) => {
  const entities = partnershipEntities(partnership);

  if (entities.length !== 2) {
    return { entities: [], isGroupToGroup: false, pinnedProvider: null, pinnedRecipient: null };
  }

  const groups = entities.filter((entity) => entity.entity_type === GROUP_ENTITY_TYPE);

  if (groups.length === entities.length) {
    return { entities, isGroupToGroup: true, pinnedProvider: null, pinnedRecipient: null };
  }

  return {
    entities,
    isGroupToGroup: false,
    pinnedProvider: groups[0] || null,
    pinnedRecipient: entities.find((entity) => entity.entity_type === RESTAURANT_ENTITY_TYPE) || null,
  };
};

/**
 * @description The recipient side's own group. The audience selectors and the card
 * list are scoped by it, and a Restaurant recipient resolves to the group above it —
 * which the row already carries, so no extra lookup is needed for a group recipient.
 * @param partnerProgramData The current partner form state.
 * @returns { restaurantGroupId, restaurantId } — either may be null.
 */
export const recipientScope = (partnerProgramData) => {
  const recipient = parseEntityKey(partnerProgramData?.recipient_key);

  if (!recipient) {
    return { restaurantGroupId: null, restaurantId: null };
  }

  return recipient.type === RESTAURANT_ENTITY_TYPE
    ? { restaurantGroupId: null, restaurantId: recipient.id }
    : { restaurantGroupId: recipient.id, restaurantId: null };
};

/** Whole-day bounds, matching what the backend's program window compares. */
export const toProgramStartTimestamp = (date) => (date ? `${date}T00:00:00Z` : null);
export const toProgramEndTimestamp = (date) => (date ? `${date}T23:59:59Z` : null);

/**
 * @description Turns a stored timestamp back into what a date input takes.
 * @param timestamp An ISO timestamp, or null.
 * @returns The yyyy-mm-dd portion, or an empty string.
 */
export const toDateInputValue = (timestamp) => `${timestamp ?? ""}`.slice(0, 10);

/**
 * @description The `audience_mode` rule value: one key holding the dimension and its
 * selection, so the two can never disagree. `all` names no selection.
 * @param partnerProgramData The current partner form state.
 * @returns The rule value for audience_mode.
 */
export const buildAudienceValue = (partnerProgramData) => {
  const mode = partnerProgramData?.audience_mode || ALL_AUDIENCE_MODE;

  return {
    mode,
    value: mode === ALL_AUDIENCE_MODE ? [] : (partnerProgramData?.audience_values || []),
  };
};

const buildUsageFrequency = (numberOfTime, numberOfDays) => (
  isTextFieldSet(numberOfTime) && isTextFieldSet(numberOfDays)
    ? { number_of_time: Number(numberOfTime), number_of_days: Number(numberOfDays) }
    : null
);

const buildTimeWindow = (startTime, endTime) => (
  isTextFieldSet(startTime) && isTextFieldSet(endTime)
    ? { start_time: startTime, end_time: endTime }
    : null
);

/**
 * @description The value the form currently holds for a partnership rule key, in the
 * shape the endpoint takes. Null means "not set", which keeps the rule out of the payload.
 * @param partnerProgramData The current partner form state.
 * @param ruleKey A PARTNERSHIP_RULE_KEYS member.
 * @returns The rule value, or null.
 */
export const partnerRuleValue = (partnerProgramData, ruleKey) => {
  switch (ruleKey) {
    case BENEFIT_CARD_IDS_KEY:
      return partnerProgramData?.benefit_card_ids || [];
    case AUDIENCE_MODE_KEY:
      return buildAudienceValue(partnerProgramData);
    case EXCEPTIONS_PERMITTED_KEY:
      return Boolean(partnerProgramData?.exceptions_permitted);
    case EXCEPTION_GUEST_IDS_KEY:
      return partnerProgramData?.exception_network_guest_ids || [];
    case USAGE_FREQUENCY_KEY:
      return buildUsageFrequency(partnerProgramData?.usage_number_of_time, partnerProgramData?.usage_number_of_days);
    case PROGRAM_START_DATE_KEY:
      return toProgramStartTimestamp(partnerProgramData?.program_start_date);
    case PROGRAM_END_DATE_KEY:
      return toProgramEndTimestamp(partnerProgramData?.program_end_date);
    case PROGRAM_VALID_DAYS_KEY:
      return partnerProgramData?.program_valid_days?.length ? partnerProgramData.program_valid_days : null;
    case PROGRAM_TIME_WINDOW_KEY:
      return buildTimeWindow(partnerProgramData?.program_valid_time_start, partnerProgramData?.program_valid_time_end);
    default:
      return null;
  }
};

/**
 * @description The envelope a `configurable` rule moves within, shaped exactly like
 * the value it bounds.
 * @param partnerProgramData The current partner form state.
 * @param ruleKey A CONFIGURABLE_RULE_KEYS member.
 * @returns The override_constraint value, or null when it is not filled in.
 */
export const partnerOverrideConstraint = (partnerProgramData, ruleKey) => {
  const constraints = partnerProgramData?.override_constraints || {};

  switch (ruleKey) {
    case USAGE_FREQUENCY_KEY:
      return buildUsageFrequency(constraints.usage_number_of_time, constraints.usage_number_of_days);
    case PROGRAM_START_DATE_KEY:
      return toProgramStartTimestamp(constraints.program_start_date);
    case PROGRAM_END_DATE_KEY:
      return toProgramEndTimestamp(constraints.program_end_date);
    case PROGRAM_VALID_DAYS_KEY:
      return constraints.program_valid_days?.length ? constraints.program_valid_days : null;
    case PROGRAM_TIME_WINDOW_KEY:
      return buildTimeWindow(constraints.program_valid_time_start, constraints.program_valid_time_end);
    default:
      return null;
  }
};

/**
 * @description The override mode a rule is authored under. The exception pair shares
 * one control, so the id list follows the boolean.
 * @param partnerProgramData The current partner form state.
 * @param ruleKey A partnership rule key.
 * @returns The override mode.
 */
export const partnerOverrideMode = (partnerProgramData, ruleKey) => {
  const modes = partnerProgramData?.override_modes || {};
  const controlKey = ruleKey === EXCEPTION_GUEST_IDS_KEY ? EXCEPTIONS_PERMITTED_KEY : ruleKey;

  return modes[controlKey] || DEFAULT_OVERRIDE_MODE;
};

/**
 * @description The prefill contract for one rule key, or null outside the recipient
 * flow. The bounds are the endpoint's — never re-derived here, which is the whole
 * reason GET /policy_engines/recipient_prefill exists.
 * @param partnerProgramData The current partner form state.
 * @param ruleKey A partnership rule key.
 * @returns The rule contract, or null.
 */
export const ruleContract = (partnerProgramData, ruleKey) => (
  (partnerProgramData?.prefill?.rules || []).find((rule) => rule?.rule_key === ruleKey) || null
);

/**
 * @description Whether a control accepts input. Everything is editable on a provider
 * policy; a recipient's is governed by the contract, and an absent contract (the
 * prefill has not landed yet) leaves the control closed rather than guessing.
 * @param partnerProgramData The current partner form state.
 * @param ruleKey A partnership rule key.
 * @returns True when the control is editable.
 */
export const isRuleEditable = (partnerProgramData, ruleKey) => {
  if (partnerProgramData?.policy_layer !== RECIPIENT_POLICY_LAYER) {
    return true;
  }

  return Boolean(ruleContract(partnerProgramData, ruleKey)?.editable);
};

const describeBound = (ruleKey, bound) => {
  if (bound === null || bound === undefined) {
    return "";
  }

  if (ruleKey === USAGE_FREQUENCY_KEY) {
    return `${bound.number_of_time} per ${bound.number_of_days} days`;
  }

  if (ruleKey === PROGRAM_TIME_WINDOW_KEY) {
    return `${bound.start_time}–${bound.end_time}`;
  }

  if (Array.isArray(bound)) {
    return bound.join(", ");
  }

  return toDateInputValue(bound) || `${bound}`;
};

/**
 * @description What the provider's terms say about a rule, for the info icon the
 * recipient form renders beside every governed label.
 * @param contract A rule entry from the prefill response.
 * @param ruleKey The rule the contract describes.
 * @param boundLabel An already-readable bound, for rules whose bound is a list of ids.
 * @returns The tooltip text, or an empty string when there is nothing to say.
 */
export const describeRuleConstraint = (contract, ruleKey, boundLabel = null) => {
  if (!contract) {
    return "";
  }

  const bound = boundLabel || describeBound(ruleKey, contract.bound);

  switch (contract.constraint_kind) {
    case "locked":
      return "The provider policy has locked this field — its value is inherited and cannot be changed.";
    case "provider_only":
      return "Set by the provider policy. The benefit cards are the offer, so the provider states them.";
    case "restrictive":
      return `The provider allows this to be narrowed only, no further than ${bound}.`;
    case "configurable":
      return `The provider allows any value within ${bound}.`;
    case "recipient_owned":
      return "The audience is yours to decide; the provider has no say in it.";
    default:
      return "The provider left this unset, so you can set it freely.";
  }
};

const withOverrideMetadata = (rule, partnerProgramData) => {
  const mode = partnerOverrideMode(partnerProgramData, rule.rule_key);
  const overridden = { ...rule, override_mode: mode };

  if (mode !== "configurable") {
    return overridden;
  }

  return { ...overridden, override_constraint: partnerOverrideConstraint(partnerProgramData, rule.rule_key) };
};

// The rules a provider states beyond the two required ones. Each ships only once its
// value is complete; an unset rule says nothing about the program rather than saying zero.
const OPTIONAL_PROGRAM_RULE_KEYS = [
  USAGE_FREQUENCY_KEY,
  PROGRAM_START_DATE_KEY,
  PROGRAM_END_DATE_KEY,
  PROGRAM_VALID_DAYS_KEY,
  PROGRAM_TIME_WINDOW_KEY,
];

/**
 * @description The `rules` array for a PROVIDER policy: the two required keys, the
 * exception pair, and every optional rule the author completed. Every rule but
 * audience_mode carries an override_mode, which the endpoint requires of a provider.
 * @param partnerProgramData The current partner form state.
 * @returns The rules array.
 */
const buildProviderRules = (partnerProgramData) => {
  const rules = [
    { rule_key: BENEFIT_CARD_IDS_KEY, rule_value: partnerRuleValue(partnerProgramData, BENEFIT_CARD_IDS_KEY), override_mode: "locked" },
    // audience_mode is override-exempt: the audience is the recipient's alone, so a
    // provider stating a mode here still says nothing about how far it may move.
    { rule_key: AUDIENCE_MODE_KEY, rule_value: buildAudienceValue(partnerProgramData) },
    withOverrideMetadata(
      { rule_key: EXCEPTIONS_PERMITTED_KEY, rule_value: Boolean(partnerProgramData?.exceptions_permitted) },
      partnerProgramData
    ),
  ];

  // The switch and the list are one setting split over two rules, coupled in both
  // directions — the list ships only while the switch is on.
  if (partnerProgramData?.exceptions_permitted) {
    rules.push(withOverrideMetadata(
      { rule_key: EXCEPTION_GUEST_IDS_KEY, rule_value: partnerRuleValue(partnerProgramData, EXCEPTION_GUEST_IDS_KEY) },
      partnerProgramData
    ));
  }

  OPTIONAL_PROGRAM_RULE_KEYS.forEach((ruleKey) => {
    const ruleValue = partnerRuleValue(partnerProgramData, ruleKey);

    if (ruleValue !== null) {
      rules.push(withOverrideMetadata({ rule_key: ruleKey, rule_value: ruleValue }, partnerProgramData));
    }
  });

  return rules;
};

/**
 * @description The `rules` array for a RECIPIENT policy. Only rules the prefill marks
 * `submit` may be sent — a locked one is displayed but omitted, and the provider's
 * value stands through effective_rule_value. No override metadata: only the provider
 * authors that.
 * @param partnerProgramData The current partner form state.
 * @returns The rules array.
 */
const buildRecipientRules = (partnerProgramData) => {
  const submittable = (ruleKey) => Boolean(ruleContract(partnerProgramData, ruleKey)?.submit);
  const rules = [];

  // audience_mode is required of the recipient — with no override there is nothing to
  // inherit, so omitting it would leave its own population unstated.
  rules.push({ rule_key: AUDIENCE_MODE_KEY, rule_value: buildAudienceValue(partnerProgramData) });

  if (submittable(EXCEPTIONS_PERMITTED_KEY)) {
    rules.push({ rule_key: EXCEPTIONS_PERMITTED_KEY, rule_value: Boolean(partnerProgramData?.exceptions_permitted) });

    if (partnerProgramData?.exceptions_permitted && submittable(EXCEPTION_GUEST_IDS_KEY)) {
      rules.push({
        rule_key: EXCEPTION_GUEST_IDS_KEY,
        rule_value: partnerRuleValue(partnerProgramData, EXCEPTION_GUEST_IDS_KEY),
      });
    }
  }

  OPTIONAL_PROGRAM_RULE_KEYS.forEach((ruleKey) => {
    const ruleValue = partnerRuleValue(partnerProgramData, ruleKey);

    if (submittable(ruleKey) && ruleValue !== null) {
      rules.push({ rule_key: ruleKey, rule_value: ruleValue });
    }
  });

  return rules;
};

/**
 * @description Builds the create-policy request body for a partner benefit program.
 * @param policyConfigurationData The shared form state, which owns the name and draft flag.
 * @param partnerProgramData The partner form state.
 * @returns The payload for POST /v1/admin/policy_engines.
 */
export const buildPartnerProgramPayload = (policyConfigurationData, partnerProgramData) => {
  const provider = parseEntityKey(partnerProgramData?.provider_key);
  const recipient = parseEntityKey(partnerProgramData?.recipient_key);
  const isProvider = partnerProgramData?.policy_layer === PROVIDER_POLICY_LAYER;

  return {
    policy_type: partnerProgramData?.policy_layer,
    name: `${policyConfigurationData?.name || ""}`.trim(),
    description: `${partnerProgramData?.description || ""}`.trim() || undefined,
    draft: Boolean(policyConfigurationData?.draft),
    partnership_provider_type: provider?.type,
    partnership_provider_id: provider?.id,
    partnership_recipient_type: recipient?.type,
    partnership_recipient_id: recipient?.id,
    rules: isProvider ? buildProviderRules(partnerProgramData) : buildRecipientRules(partnerProgramData),
  };
};

/**
 * The label each partner field is shown under, so a message names the control the
 * author has to go back to rather than the rule_key behind it.
 */
export const PARTNER_FIELD_LABELS = {
  name: "Policy name",
  policy_layer: "Policy layer",
  partnership_id: "Partnership",
  provider_key: "Provider",
  recipient_key: "Recipient",
  benefit_card_ids: "Benefit",
  audience_values: "Audience selection",
  exception_network_guest_ids: "Excepted users",
  usage_frequency: "Usage frequency",
  program_start_date: "Program start date",
  program_end_date: "Program end date",
  program_valid_days: "Valid days",
  program_valid_time_window: "Valid time window",
  usage_frequency_range: "Usage frequency range",
  program_start_date_range: "Program start date range",
  program_end_date_range: "Program end date range",
  program_valid_days_range: "Valid days range",
  program_valid_time_window_range: "Valid time window range",
};

// Each configurable rule's own value, named by the field its message reports under.
const CONFIGURABLE_RULE_FIELDS = {
  [USAGE_FREQUENCY_KEY]: "usage_frequency",
  [PROGRAM_START_DATE_KEY]: "program_start_date",
  [PROGRAM_END_DATE_KEY]: "program_end_date",
  [PROGRAM_VALID_DAYS_KEY]: "program_valid_days",
  [PROGRAM_TIME_WINDOW_KEY]: "program_valid_time_window",
};

// Each configurable rule's envelope, named by the field its message reports under.
const CONFIGURABLE_RANGE_FIELDS = {
  [USAGE_FREQUENCY_KEY]: "usage_frequency_range",
  [PROGRAM_START_DATE_KEY]: "program_start_date_range",
  [PROGRAM_END_DATE_KEY]: "program_end_date_range",
  [PROGRAM_VALID_DAYS_KEY]: "program_valid_days_range",
  [PROGRAM_TIME_WINDOW_KEY]: "program_valid_time_window_range",
};

/**
 * @description Every required partner field the author has left blank, in the order
 * the form shows them. Mirrors the asterisks the form renders, so the endpoint is
 * never asked to reject a program the form could already tell was incomplete.
 * @param policyConfigurationData The shared form state (name, draft).
 * @param partnerProgramData The partner form state.
 * @param isEditMode Whether the modal is editing a persisted program.
 * @returns The blank required field names.
 */
export const collectMissingPartnerProgramFields = (
  policyConfigurationData, partnerProgramData, isEditMode = false
) => {
  const isProvider = partnerProgramData?.policy_layer === PROVIDER_POLICY_LAYER;
  const missing = [];

  if (!`${policyConfigurationData?.name || ""}`.trim()) {
    missing.push("name");
  }

  if (!partnerProgramData?.policy_layer) {
    missing.push("policy_layer");
  }

  // Not rendered in edit mode: the pair cannot move, so the partnership behind it is
  // not asked for — and the show payload carries no partnership id to satisfy it with.
  if (!isEditMode && !partnerProgramData?.partnership_id) {
    missing.push("partnership_id");
  }

  if (!partnerProgramData?.provider_key) {
    missing.push("provider_key");
  }

  if (!partnerProgramData?.recipient_key) {
    missing.push("recipient_key");
  }

  // Provider only: the cards ARE the offer, so a recipient neither may nor must send them.
  if (isProvider && !partnerProgramData?.benefit_card_ids?.length) {
    missing.push("benefit_card_ids");
  }

  // `all` names no selection; every other mode has to name at least one.
  if (partnerProgramData?.audience_mode !== ALL_AUDIENCE_MODE && !partnerProgramData?.audience_values?.length) {
    missing.push("audience_values");
  }

  if (partnerProgramData?.exceptions_permitted && !partnerProgramData?.exception_network_guest_ids?.length) {
    missing.push("exception_network_guest_ids");
  }

  if (!isProvider) {
    return missing;
  }

  // Configurable means "the recipient may vary this within a range", so it needs both
  // halves: an unset rule ships no range at all, and a range bounds nothing without one.
  CONFIGURABLE_RULE_KEYS.forEach((ruleKey) => {
    if (partnerOverrideMode(partnerProgramData, ruleKey) !== "configurable") {
      return;
    }

    if (partnerRuleValue(partnerProgramData, ruleKey) === null) {
      missing.push(CONFIGURABLE_RULE_FIELDS[ruleKey]);
      return;
    }

    if (partnerOverrideConstraint(partnerProgramData, ruleKey) === null) {
      missing.push(CONFIGURABLE_RANGE_FIELDS[ruleKey]);
    }
  });

  return missing;
};

/**
 * The partner fields that render a message beside them. A problem named nowhere else
 * has to reach the snackbar, or a control scrolled out of view would report nothing.
 */
export const PARTNER_INLINE_MESSAGE_FIELDS = [
  "name",
  "policy_layer",
  "partnership_id",
  "provider_key",
  "recipient_key",
  "benefit_card_ids",
  "audience_values",
  "usage_frequency",
  "program_start_date",
  "program_end_date",
  "program_valid_time_window",
];

/**
 * @description The blank required partner fields named by their form label.
 * @param policyConfigurationData The shared form state.
 * @param partnerProgramData The partner form state.
 * @param isEditMode Whether the modal is editing a persisted program.
 * @returns The labels of the blank required fields.
 */
export const describeMissingPartnerProgramFields = (
  policyConfigurationData, partnerProgramData, isEditMode = false
) => (
  collectMissingPartnerProgramFields(policyConfigurationData, partnerProgramData, isEditMode)
    .map((field) => PARTNER_FIELD_LABELS[field])
);

/**
 * @description Validates the parts of the program the endpoint would otherwise reject
 * with a 422, so the author sees the problem next to the field.
 * @param policyConfigurationData The shared form state.
 * @param partnerProgramData The partner form state.
 * @param isEditMode Whether the modal is editing a persisted program.
 * @returns A map of field name to error message; empty when the program is valid.
 */
export const validatePartnerProgram = (policyConfigurationData, partnerProgramData, isEditMode = false) => {
  const errors = {};

  if (!`${policyConfigurationData?.name || ""}`.trim()) {
    errors.name = "Policy name is required";
  }

  if (!partnerProgramData?.policy_layer) {
    errors.policy_layer = "Select a policy layer";
  }

  if (!isEditMode && !partnerProgramData?.partnership_id) {
    errors.partnership_id = "Select a partnership";
  }

  if (!partnerProgramData?.provider_key) {
    errors.provider_key = "Select a provider";
  }

  if (!partnerProgramData?.recipient_key) {
    errors.recipient_key = "Select a recipient";
  }

  if (partnerProgramData?.provider_key
    && partnerProgramData.provider_key === partnerProgramData?.recipient_key) {
    errors.recipient_key = "Provider and recipient must be different sides";
  }

  const startDate = partnerProgramData?.program_start_date;
  const endDate = partnerProgramData?.program_end_date;

  // Both bounds or neither — one alone describes no window at all.
  if (startDate && !endDate) {
    errors.program_end_date = "Set a program end date to go with the start date";
  }

  if (endDate && !startDate) {
    errors.program_start_date = "Set a program start date to go with the end date";
  }

  if (startDate && endDate && toProgramEndTimestamp(endDate) <= toProgramStartTimestamp(startDate)) {
    errors.program_end_date = "Program end date must be after the start date";
  }

  const hasUsageTimes = isTextFieldSet(partnerProgramData?.usage_number_of_time);
  const hasUsageDays = isTextFieldSet(partnerProgramData?.usage_number_of_days);

  if (hasUsageTimes !== hasUsageDays) {
    errors.usage_frequency = "Usage frequency needs both a number of times and a number of days";
  }

  if ((hasUsageTimes && Number(partnerProgramData.usage_number_of_time) < 1)
    || (hasUsageDays && Number(partnerProgramData.usage_number_of_days) < 1)) {
    errors.usage_frequency = "Usage frequency must be a positive number of times and days";
  }

  const hasStartTime = isTextFieldSet(partnerProgramData?.program_valid_time_start);
  const hasEndTime = isTextFieldSet(partnerProgramData?.program_valid_time_end);

  if (hasStartTime !== hasEndTime) {
    errors.program_valid_time_window = "Set both a from and a to time";
  }

  return errors;
};

/**
 * @description Maps GET /policy_engines/recipient_prefill onto the recipient form, so
 * it opens on exactly the terms the provider stated. Values arrive in their JSON
 * shapes; the number and date inputs are text fields, so they are converted here.
 * @param prefill The prefill response.
 * @param partnerProgramData The current partner form state, whose pair is preserved.
 * @returns The next partner form state.
 */
export const mapPrefillToPartnerProgram = (prefill, partnerProgramData) => {
  const contracts = (prefill?.rules || []).reduce(
    (map, rule) => ({ ...map, [rule?.rule_key]: rule }),
    {}
  );
  const defaultValue = (ruleKey) => contracts[ruleKey]?.default_value ?? null;

  const usageFrequency = defaultValue(USAGE_FREQUENCY_KEY) || {};
  const timeWindow = defaultValue(PROGRAM_TIME_WINDOW_KEY) || {};
  const audience = defaultValue(AUDIENCE_MODE_KEY) || {};

  return {
    ...partnerProgramData,
    prefill,
    benefit_card_ids: defaultValue(BENEFIT_CARD_IDS_KEY) || [],
    exceptions_permitted: Boolean(defaultValue(EXCEPTIONS_PERMITTED_KEY)),
    exception_network_guest_ids: defaultValue(EXCEPTION_GUEST_IDS_KEY) || [],
    usage_number_of_time: `${usageFrequency.number_of_time ?? ""}`,
    usage_number_of_days: `${usageFrequency.number_of_days ?? ""}`,
    program_start_date: toDateInputValue(defaultValue(PROGRAM_START_DATE_KEY)),
    program_end_date: toDateInputValue(defaultValue(PROGRAM_END_DATE_KEY)),
    program_valid_days: defaultValue(PROGRAM_VALID_DAYS_KEY) || [],
    program_valid_time_start: timeWindow.start_time || "",
    program_valid_time_end: timeWindow.end_time || "",
    audience_mode: audience.mode || ALL_AUDIENCE_MODE,
    audience_values: audienceValuesFrom(audience),
    // A recipient authors no override metadata, so the provider's stays where it is.
    override_modes: { ...DEFAULT_OVERRIDE_MODES },
    override_constraints: { ...INITIAL_OVERRIDE_CONSTRAINTS },
  };
};

// Maps a persisted rule onto the form fields that represent it — the inverse of
// partnerRuleValue. Values arrive as their JSON types; the number and date inputs
// are text fields, so they are converted here.
const PARTNER_RULE_MAPPERS = {
  [BENEFIT_CARD_IDS_KEY]: (ruleValue) => ({
    benefit_card_ids: Array.isArray(ruleValue) ? ruleValue : [],
  }),
  [AUDIENCE_MODE_KEY]: (ruleValue) => ({
    audience_mode: ruleValue?.mode || ALL_AUDIENCE_MODE,
    audience_values: audienceValuesFrom(ruleValue),
  }),
  [EXCEPTIONS_PERMITTED_KEY]: (ruleValue) => ({ exceptions_permitted: Boolean(ruleValue) }),
  [EXCEPTION_GUEST_IDS_KEY]: (ruleValue) => ({
    exception_network_guest_ids: Array.isArray(ruleValue) ? ruleValue : [],
  }),
  [USAGE_FREQUENCY_KEY]: (ruleValue) => ({
    usage_number_of_time: `${ruleValue?.number_of_time ?? ""}`,
    usage_number_of_days: `${ruleValue?.number_of_days ?? ""}`,
  }),
  [PROGRAM_START_DATE_KEY]: (ruleValue) => ({ program_start_date: toDateInputValue(ruleValue) }),
  [PROGRAM_END_DATE_KEY]: (ruleValue) => ({ program_end_date: toDateInputValue(ruleValue) }),
  [PROGRAM_VALID_DAYS_KEY]: (ruleValue) => ({
    program_valid_days: Array.isArray(ruleValue) ? ruleValue : [],
  }),
  [PROGRAM_TIME_WINDOW_KEY]: (ruleValue) => ({
    program_valid_time_start: ruleValue?.start_time || "",
    program_valid_time_end: ruleValue?.end_time || "",
  }),
};

// The envelope is shaped exactly like the value it bounds, but lands in the
// override_constraints fields rather than the value's own — the inverse of
// partnerOverrideConstraint.
const PARTNER_CONSTRAINT_MAPPERS = {
  [USAGE_FREQUENCY_KEY]: (envelope) => ({
    usage_number_of_time: `${envelope?.number_of_time ?? ""}`,
    usage_number_of_days: `${envelope?.number_of_days ?? ""}`,
  }),
  [PROGRAM_START_DATE_KEY]: (envelope) => ({ program_start_date: toDateInputValue(envelope) }),
  [PROGRAM_END_DATE_KEY]: (envelope) => ({ program_end_date: toDateInputValue(envelope) }),
  [PROGRAM_VALID_DAYS_KEY]: (envelope) => ({
    program_valid_days: Array.isArray(envelope) ? envelope : [],
  }),
  [PROGRAM_TIME_WINDOW_KEY]: (envelope) => ({
    program_valid_time_start: envelope?.start_time || "",
    program_valid_time_end: envelope?.end_time || "",
  }),
};

// hasOwnProperty, not a bare lookup: a rule_key colliding with an inherited Object
// member ("constructor") would otherwise resolve to a truthy non-mapper.
const ruleMapper = (mappers, ruleKey) => (
  Object.prototype.hasOwnProperty.call(mappers, `${ruleKey}`) ? mappers[`${ruleKey}`] : null
);

/**
 * @description A dropdown-shaped partnership row built from a policy's two program
 * sides. The show payload names both sides but carries no RestaurantGroupPartnership
 * id, and the side selects read their labels off the row — so an edit synthesizes one
 * rather than fetching the partnership list to find it.
 * @param policy The policy returned by GET /v1/admin/policy_engines/:id.
 * @returns A row derivePartnershipSides can resolve, or null.
 */
export const programSidesToPartnership = (policy) => {
  const toSide = (side) => (side?.type && side?.id
    ? { entity_type: side.type, entity_id: side.id, entity_name: side.name || `${side.id}` }
    : null);

  const provider = toSide(policy?.partnership_provider);
  const recipient = toSide(policy?.partnership_recipient);

  if (!provider || !recipient) {
    return null;
  }

  const label = `${provider.entity_name} / ${recipient.entity_name}`;

  return { id: null, relationship_name: label, label, entity_one: provider, entity_two: recipient };
};

// `{}` is the column's default and how an unset envelope comes back, so it is absent
// rather than a value — otherwise every non-configurable rule would read as changed.
const hasEnvelope = (envelope) => {
  if (envelope === null || envelope === undefined) {
    return false;
  }

  return Array.isArray(envelope) ? envelope.length > 0 : Object.keys(envelope).length > 0;
};

/** Drops the ids the show endpoint returns, leaving only what an update sends. */
const toPartnerRulePairs = (rules) => (
  (Array.isArray(rules) ? rules : []).map((rule) => {
    const pair = { rule_key: rule?.rule_key, rule_value: rule?.rule_value };

    if (rule?.override_mode) {
      pair.override_mode = rule.override_mode;
    }

    if (hasEnvelope(rule?.override_constraint)) {
      pair.override_constraint = rule.override_constraint;
    }

    return pair;
  })
);

// jsonb reads a hash back in its own key order, so a plain JSON.stringify could call
// two identical rule values different. Keys are sorted before they are compared.
const stableStringify = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }

  return JSON.stringify(value ?? null);
};

/**
 * @description A rule's value as compared, with job-role titles normalized so a legacy
 * un-normalized row does not read as edited.
 * @param rule A program rule.
 * @returns The comparable rule value.
 */
const canonicalizeRuleValue = (rule) => (
  rule.rule_key === AUDIENCE_MODE_KEY && rule.rule_value?.mode === JOB_ROLE_AUDIENCE_MODE
    ? { ...rule.rule_value, value: audienceValuesFrom(rule.rule_value) }
    : rule.rule_value ?? null
);

/**
 * @description Order-insensitive signature of a program rule set, used to tell an
 * edited one from an untouched one. Override metadata is part of the rule, so a
 * mode-only change on a provider policy counts — otherwise it would read as
 * untouched and never be sent.
 * @param rules A program rule set.
 * @returns A string that is equal for equivalent rule sets.
 */
const canonicalizePartnerRules = (rules) => stableStringify(
  toPartnerRulePairs(rules)
    .map((rule) => ({ ...rule, rule_key: `${rule.rule_key}`, rule_value: canonicalizeRuleValue(rule) }))
    .sort((left, right) => left.rule_key.localeCompare(right.rule_key))
);

/**
 * @description Maps a benefit program fetched from the show endpoint onto the partner
 * form state, so the edit modal opens on exactly what is persisted. The rule set is
 * kept verbatim as `persisted_rules` for the update to diff against.
 * @param policy The policy returned by GET /v1/admin/policy_engines/:id.
 * @returns Partner form state for PartnerBenefitProgramForm.
 */
export const mapPartnerPolicyToConfiguration = (policy) => {
  const rules = Array.isArray(policy?.rules) ? policy.rules : [];
  const isProvider = policy?.policy_type === PROVIDER_POLICY_LAYER;

  const ruleFields = rules.reduce((accumulator, rule) => {
    const mapRule = ruleMapper(PARTNER_RULE_MAPPERS, rule?.rule_key);
    return mapRule ? { ...accumulator, ...mapRule(rule?.rule_value) } : accumulator;
  }, {});

  const overrideModes = { ...DEFAULT_OVERRIDE_MODES };
  const overrideConstraints = { ...INITIAL_OVERRIDE_CONSTRAINTS };

  // Only a provider policy authors override metadata; the serializer omits it entirely
  // on a recipient's rules, so its controls stay on their defaults.
  if (isProvider) {
    rules.forEach((rule) => {
      if (rule?.override_mode && OVERRIDABLE_RULE_KEYS.includes(rule.rule_key)) {
        overrideModes[rule.rule_key] = rule.override_mode;
      }

      const mapConstraint = rule?.override_mode === "configurable"
        ? ruleMapper(PARTNER_CONSTRAINT_MAPPERS, rule?.rule_key)
        : null;

      if (mapConstraint) {
        Object.assign(overrideConstraints, mapConstraint(rule?.override_constraint));
      }
    });
  }

  return {
    ...INITIAL_PARTNER_PROGRAM_CONFIGURATION,
    policy_layer: policy?.policy_type || "",
    partnership: programSidesToPartnership(policy),
    provider_key: buildEntityKey(policy?.partnership_provider_type, policy?.partnership_provider_id),
    recipient_key: buildEntityKey(policy?.partnership_recipient_type, policy?.partnership_recipient_id),
    description: policy?.description || "",
    status: policy?.status || "",
    persisted_rules: toPartnerRulePairs(rules),
    override_modes: overrideModes,
    override_constraints: overrideConstraints,
    ...ruleFields,
  };
};

/**
 * @description The edit-mode counterpart of mapPrefillToPartnerProgram. It stores the
 * contract, so isRuleEditable and describeRuleConstraint work exactly as on create,
 * and fills in a default ONLY for rules the recipient holds no persisted value for —
 * a locked or provider-only rule was never in the recipient's own set, so the
 * provider's current value is its only source. A persisted value is never overwritten.
 * @param prefill The prefill response.
 * @param partnerProgramData The current partner form state, already seeded from the policy.
 * @returns The next partner form state.
 */
export const mergePrefillContract = (prefill, partnerProgramData) => {
  const persistedKeys = new Set(
    (partnerProgramData?.persisted_rules || []).map((rule) => `${rule?.rule_key}`)
  );

  const inheritedFields = (prefill?.rules || []).reduce((accumulator, rule) => {
    if (!rule?.rule_key || persistedKeys.has(`${rule.rule_key}`)) {
      return accumulator;
    }

    const mapRule = ruleMapper(PARTNER_RULE_MAPPERS, rule.rule_key);

    return mapRule && rule.default_value !== null && rule.default_value !== undefined
      ? { ...accumulator, ...mapRule(rule.default_value) }
      : accumulator;
  }, {});

  return { ...partnerProgramData, ...inheritedFields, prefill };
};

// A program the scheduler expired at its end date. Out of force, but unlike `archived`
// it is a lapsed window rather than a deliberate retirement.
const EXPIRED_POLICY_STATUS = "expired";

/**
 * @description Whether the submitted window would let the program run again. Mirrors the
 * endpoint's own check, which passes an open-ended window and refuses one already gone.
 * @param programEndDate The end date in the form, as yyyy-mm-dd.
 * @returns True when activating would not be refused for a lapsed window.
 */
const windowAllowsActivation = (programEndDate) => {
  const endsAt = toProgramEndTimestamp(programEndDate);

  return !endsAt || Date.parse(endsAt) > Date.now();
};

/**
 * @description The status an update should send, or undefined to leave it untouched.
 * Only a moved toggle counts, except on an expired program, where the toggle already
 * sits off and moving the dates is itself the request to run it again.
 *
 * An expired program whose dates have NOT moved forward asks for nothing: sending
 * `active` there would be requesting an activation the endpoint must refuse for a lapsed
 * window, which would cost the admin the rename they actually came to make.
 * @param previousStatus The policy's persisted status.
 * @param isDraft Whether the draft toggle is on.
 * @param programEndDate The end date in the form, as yyyy-mm-dd.
 * @returns The status to send, or undefined.
 */
const resolvePartnerProgramStatus = (previousStatus, isDraft, programEndDate) => {
  const wasDraft = previousStatus === "draft";

  if (isDraft !== wasDraft) {
    return isDraft ? "draft" : "active";
  }

  if (previousStatus !== EXPIRED_POLICY_STATUS || isDraft) {
    return undefined;
  }

  return windowAllowsActivation(programEndDate) ? "active" : undefined;
};

/**
 * @description Builds the update request body for a benefit program. Neither the
 * policy_type nor the pair is sent: those are the program's identity, and the
 * endpoint does not accept them.
 * @param policyConfigurationData The shared form state, which owns the name and draft flag.
 * @param partnerProgramData The partner form state.
 * @returns The payload for PUT /v1/admin/policy_engines/:id.
 */
export const buildPartnerProgramUpdatePayload = (policyConfigurationData, partnerProgramData) => {
  const isProvider = partnerProgramData?.policy_layer === PROVIDER_POLICY_LAYER;
  const isDraft = Boolean(policyConfigurationData?.draft);
  const nextRules = isProvider
    ? buildProviderRules(partnerProgramData)
    : buildRecipientRules(partnerProgramData);

  return {
    name: `${policyConfigurationData?.name || ""}`.trim(),
    description: `${partnerProgramData?.description || ""}`.trim(),
    status: resolvePartnerProgramStatus(partnerProgramData?.status, isDraft, partnerProgramData?.program_end_date),
    // Omitted when nothing moved: supplying `rules` makes the endpoint re-run every
    // cross-rule check, which would otherwise let terms tightened since block an
    // edit that only renamed the program.
    rules: canonicalizePartnerRules(nextRules) === canonicalizePartnerRules(partnerProgramData?.persisted_rules)
      ? undefined
      : nextRules,
  };
};

export const NO_ACTIVE_PROVIDER_POLICY_TEXT =
  "No active provider policy exists for this partnership. A provider policy has to be live before "
  + "the recipient side can be authored.";

export const PARTNER_PROGRAM_CLEAR_TEXT = "No active policy found you can proceed to create";
export const PARTNER_PROGRAM_CHECKING_TEXT = "Checking for active policy...";
